<script setup lang="ts">
import Button from '../General/Button.vue';
import raiseHandIcon from '../../assets/icons/raise-hand.svg';
import { computed, ref, watch } from 'vue';
import { useClassStore, useSignalRStore, useUserStore } from '../../stores';
import { useDraggable, useWindowSize } from '@vueuse/core';

const signalr = useSignalRStore()
const classStore = useClassStore()
const user = useUserStore()

const handEl = ref<HTMLElement | null>(null)
const isRaisedHand = computed(() => classStore.raiseHandStudentIds.includes(user.userInfo?.id || 0))

let isMoved: boolean = false
const { width, height } = useWindowSize()
const handElSize = 84
const paddingOffset = 20
const pos: { x: number, y: number } = { x: 0, y: 0 }
const allowdedMoveOffset = 2
const { x, y, style, isDragging } = useDraggable(handEl, {
    initialValue: {
        x: width.value - handElSize - paddingOffset,
        y: height.value - handElSize - paddingOffset
    },
    onStart: () => {
        isMoved = false
        pos.x = x.value
        pos.y = y.value
    },
    onEnd: () => {
        if (Math.abs(x.value - pos.x) > allowdedMoveOffset || Math.abs(y.value - pos.y) > allowdedMoveOffset) {
            isMoved = true
        }

        setRaiseHandPosition()
    }
})

watch([width, height], () => {
    setRaiseHandPosition()
})

function setRaiseHandPosition() {
    // snap position x
    if (x.value < width.value / 2) {
        x.value = paddingOffset
    } else {
        x.value = width.value - handElSize - paddingOffset
    }

    // set boundary y
    y.value = Math.min(
        Math.max(paddingOffset, y.value),
        height.value - handElSize - paddingOffset
    )
}

function handleRaiseHandClick() {
    if (isMoved) return;
    if (isRaisedHand.value || !classStore.selectedClassDetails?.allowRaiseHand) return;
    signalr.raiseHand()
}
</script>

<template>
    <Teleport to="body">
        <div ref="handEl" :style="style" class="fixed z-40 touch-none"
            :class="{ 'transition-all duration-300': !isDragging }">
            <Button :color="isRaisedHand ? 'warning' : 'light'" design="pill" class="!p-4"
                @click="handleRaiseHandClick">
                <img :src="raiseHandIcon"
                    class="!size-12 !max-w-12 !max-h-12 select-none pointer-events-none touch-none" />
            </Button>
            <div v-if="!isRaisedHand" class="absolute inset-0 flex items-center justify-center z-[-1]">
                <div class="size-16 rounded-full bg-neutral-200 animate-ping"></div>
            </div>
        </div>
    </Teleport>
</template>